import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { apiUrl } from '../api';
import { CheckCircle, AlertCircle } from 'lucide-react';

// Public quote-request form. Submits to /api/inquiry which writes a real
// lead into the GreenTree CRM (Firestore `leads`, status LEAD, source public-web).
const serviceOptions = [
  'Tree Care & Removal',
  'Drone Inspection Services',
  'Tree Health & Risk Assessment',
  'Consulting & Arborist Reports',
  'Emergency Storm Response',
  'Something else',
];

const PublicQuoteForm: React.FC = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', serviceType: serviceOptions[0], message: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');
  const [ticket, setTicket] = useState('');

  // Self-service booking: load the owner's genuinely free estimate windows.
  const [slotDays, setSlotDays] = useState<{ date: string; label: string; slots: string[] }[]>([]);
  const [slotDay, setSlotDay] = useState('');
  const [slotTime, setSlotTime] = useState('');

  useEffect(() => {
    let alive = true;
    fetch(apiUrl('/api/estimate/slots'))
      .then(r => r.json())
      .then(out => {
        if (!alive || !out?.days?.length) return;
        setSlotDays(out.days);
        setSlotDay(out.days[0].date);
      })
      .catch(() => { /* booking is optional - the plain request still works */ });
    return () => { alive = false; };
  }, []);

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(prev => ({ ...prev, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'sending') return;
    if (!form.name.trim() || (!form.email.trim() && !form.phone.trim())) {
      setError('Please give us your name and at least one way to reach you.');
      setStatus('error');
      return;
    }
    setStatus('sending');
    setError('');
    try {
      const booking = !!(slotDay && slotTime);
      const res = await fetch(apiUrl(booking ? '/api/estimate/book' : '/api/inquiry'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(booking ? { ...form, date: slotDay, time: slotTime } : form),
      });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(out.error || `HTTP ${res.status}`);
      setTicket(out.id || '');
      setStatus('success');
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please call (706) 326-7034.');
      setStatus('error');
    }
  };

  const inputCls = "w-full p-3 bg-white border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gecko/40 focus:border-gecko";
  const labelCls = "block text-xs font-black text-stone-500 uppercase tracking-wider mb-1.5";

  return (
    <div id="quote" className="max-w-3xl mx-auto scroll-mt-24">
      <div className="text-center mb-10">
        <span className="text-gecko font-black text-sm uppercase tracking-widest">Free Estimate</span>
        <h2 className="text-3xl sm:text-4xl font-black text-bark mt-3 mb-4">Request Your Free Quote</h2>
        <p className="text-stone-500 max-w-xl mx-auto">
          Tell us about your trees and we'll schedule a free on-site assessment. No obligation, no pressure.
        </p>
      </div>

      {status === 'success' ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gecko/5 border border-gecko/30 rounded-2xl p-8 text-center"
        >
          <CheckCircle className="mx-auto text-gecko" size={44} />
          <h3 className="text-xl font-black text-bark mt-4 mb-2">Request received — thank you!</h3>
          <p className="text-stone-600 text-sm max-w-md mx-auto">
            Your request has been logged in our system{ticket ? <> as <strong>{ticket}</strong></> : null} and our
            team will reach out within one business day. For emergencies, call us 24/7 at{' '}
            <a href="tel:7063267034" className="font-bold text-gecko hover:underline">(706) 326-7034</a>.
          </p>
        </motion.div>
      ) : (
        <form onSubmit={submit} className="bg-white rounded-2xl border border-stone-100 shadow-sm p-6 sm:p-8 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls} htmlFor="name-input">Name *</label>
              <input id="name-input" name="name-input" type="text" value={form.name} onChange={set('name')} className={inputCls} placeholder="Your name" required />
            </div>
            <div>
              <label className={labelCls} htmlFor="service-needed-input">Service Needed</label>
              <select id="service-needed-input" name="service-needed-input" value={form.serviceType} onChange={set('serviceType')} className={inputCls}>
                {serviceOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls} htmlFor="email-input">Email</label>
              <input id="email-input" name="email-input" type="email" value={form.email} onChange={set('email')} className={inputCls} placeholder="you@example.com" />
            </div>
            <div>
              <label className={labelCls} htmlFor="phone-input">Phone</label>
              <input id="phone-input" name="phone-input" type="tel" value={form.phone} onChange={set('phone')} className={inputCls} placeholder="(706) 555-0123" />
            </div>
          </div>
          <div>
            <label className={labelCls} htmlFor="property-address-input">Property Address</label>
            <input id="property-address-input" name="property-address-input" type="text" value={form.address} onChange={set('address')} className={inputCls} placeholder="Street, city (helps us schedule the visit)" />
          </div>
          <div>
            <label className={labelCls} htmlFor="what-s-going-on-with-your-tree-input">What's going on with your trees?</label>
            <textarea id="what-s-going-on-with-your-tree-input" name="what-s-going-on-with-your-tree-input" value={form.message} onChange={set('message')} rows={4} className={`${inputCls} resize-none`}
              placeholder="e.g. Large oak leaning after the storm, maple needs pruning, want a health check for 3 trees…" />
          </div>
          {slotDays.length > 0 && (
            <div className="bg-stone-50 border border-stone-200 rounded-2xl p-4 sm:p-5">
              <label className={labelCls}>Pick your estimate time (optional)</label>
              <p className="text-xs text-stone-500 mb-3">
                A free 45-minute visit between 9am and 8pm. Choose a day, then a time - only open slots are shown.
              </p>
              <div className="flex flex-wrap gap-2 mb-3">
                {slotDays.map(d => (
                  <button
                    key={d.date}
                    type="button"
                    onClick={() => { setSlotDay(d.date); setSlotTime(''); }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${slotDay === d.date ? 'bg-gecko text-white' : 'bg-white border border-stone-200 text-bark hover:bg-stone-50'}`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
              {slotDay && (
                <div className="flex flex-wrap gap-2">
                  {(slotDays.find(d => d.date === slotDay)?.slots || []).map(t => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setSlotTime(t)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${slotTime === t ? 'bg-palm text-white' : 'bg-white border border-stone-200 text-bark hover:bg-stone-50'}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              )}
              {slotTime && (
                <p className="mt-3 text-xs font-bold text-gecko">
                  Booked for {slotDays.find(d => d.date === slotDay)?.label} at {slotTime} - we'll confirm by email.
                </p>
              )}
            </div>
          )}
          {status === 'error' && (
            <p className="flex items-center gap-2 text-sm font-bold text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2.5">
              <AlertCircle size={16} /> {error}
            </p>
          )}
          <button
            type="submit"
            disabled={status === 'sending'}
            className="w-full py-4 bg-gecko text-white rounded-xl font-black text-base hover:bg-palm transition shadow-lg shadow-gecko/20 disabled:opacity-60"
          >
            {status === 'sending' ? 'Sending…' : (slotTime ? 'Book My Estimate →' : 'Request My Free Quote →')}
          </button>
          <p className="text-[11px] text-stone-400 text-center">
            We only use your info to respond to this request — see our <a href="/privacy.html" className="underline hover:text-stone-600">Privacy Policy</a>.
          </p>
        </form>
      )}
    </div>
  );
};

export default PublicQuoteForm;
